import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  apiError, SessionInput, TrainerSlot, TrainerSlotName, trainingService, TrainingOutcome, TrainingSession, TrainingVenue,
} from '../../api/trainingService';

export const useTrainingRoles = () => {
  const { user } = useAuth();
  const has = (name: string) => user?.roles?.some((r) => (typeof r === 'string' ? r : r.name) === name) || false;
  const isStaff = has('admin') || has('staff');
  const isTrainer = has('trainer');
  return { isStaff, isTrainer, userId: user?.id as number | undefined };
};

export const inputClass = 'border border-gray-300 rounded-lg px-3 py-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
export const buttonClass = 'inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50';
export const secondaryButtonClass = 'inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50';

export const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    published: 'bg-green-100 text-green-800',
    archived: 'bg-gray-200 text-gray-600',
    cancelled: 'bg-red-100 text-red-700',
    enrolled: 'bg-blue-100 text-blue-800',
    withdrawn: 'bg-gray-200 text-gray-600',
    completed: 'bg-green-100 text-green-800',
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

export const ErrorText: React.FC<{ error: unknown; fallback?: string }> = ({ error, fallback }) => (
  error ? <p role="alert" className="text-sm text-red-700">{apiError(error, fallback)}</p> : null
);

// ---------- Venue editing ----------

const emptyVenue = { name: '', addressLine1: '', addressLine2: '', city: '', state: 'MS', zip: '', notes: '' };

export const VenueForm: React.FC<{
  venue?: TrainingVenue | null;
  onSaved: (venue: TrainingVenue) => void;
  onCancel: () => void;
}> = ({ venue, onSaved, onCancel }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    ...emptyVenue,
    ...(venue ? Object.fromEntries(Object.keys(emptyVenue).map((k) => [k, (venue as any)[k] || ''])) : {}),
  });
  const save = useMutation({
    mutationFn: () => (venue ? trainingService.updateVenue(venue.id, form) : trainingService.createVenue(form)),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['training'] });
      onSaved(saved);
    },
  });
  const field = (key: keyof typeof emptyVenue, label: string, className = '') => (
    <label className={`block text-sm ${className}`}>
      <span className="text-gray-700">{label}</span>
      <input className={inputClass} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </label>
  );
  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        {field('name', 'Venue name', 'sm:col-span-6')}
        {field('addressLine1', 'Street address', 'sm:col-span-4')}
        {field('addressLine2', 'Suite / building', 'sm:col-span-2')}
        {field('city', 'City', 'sm:col-span-3')}
        {field('state', 'State', 'sm:col-span-1')}
        {field('zip', 'ZIP', 'sm:col-span-2')}
      </div>
      <label className="block text-sm">
        <span className="text-gray-700">Notes (parking, entrance, room)</span>
        <textarea className={inputClass} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </label>
      <div className="flex gap-2">
        <button className={buttonClass} disabled={save.isPending || !form.name.trim()} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : venue ? 'Save venue' : 'Add venue'}
        </button>
        <button className={secondaryButtonClass} onClick={onCancel}>Cancel</button>
      </div>
      <ErrorText error={save.error} fallback="Unable to save venue" />
    </div>
  );
};

/** Pick a venue for a session and edit or add venue addresses in place. */
export const VenuePicker: React.FC<{
  value: number | null;
  onChange: (venueId: number | null) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const venues = useQuery({ queryKey: ['training', 'venues'], queryFn: trainingService.venues });
  const [editing, setEditing] = useState<'new' | 'current' | null>(null);
  const current = venues.data?.find((v) => v.id === value) || null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 items-center">
        <select
          aria-label="Venue"
          className={`${inputClass} sm:w-auto sm:min-w-[16rem]`}
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Location to be announced</option>
          {(venues.data || []).filter((v) => v.isActive || v.id === value).map((v) => (
            <option key={v.id} value={v.id}>{v.name}{v.city ? ` — ${v.city}` : ''}</option>
          ))}
        </select>
        {!disabled && current && <button type="button" className={secondaryButtonClass} onClick={() => setEditing('current')}>Edit address</button>}
        {!disabled && <button type="button" className={secondaryButtonClass} onClick={() => setEditing('new')}>New venue</button>}
      </div>
      {current && editing !== 'current' && (
        <p className="text-sm text-gray-600">
          {current.address || <span className="text-amber-700">No street address yet</span>}
          {current.mapUrl && <> · <a className="text-primary-700 underline" href={current.mapUrl} target="_blank" rel="noreferrer">Map</a></>}
        </p>
      )}
      {editing && (
        <VenueForm
          venue={editing === 'current' ? current : null}
          onCancel={() => setEditing(null)}
          onSaved={(v) => {
            setEditing(null);
            if (editing === 'new') onChange(v.id);
          }}
        />
      )}
    </div>
  );
};

// ---------- Session form (staff) ----------

export const OutcomesEditor: React.FC<{ value: TrainingOutcome[]; onChange: (v: TrainingOutcome[]) => void }> = ({ value, onChange }) => (
  <div className="space-y-2">
    {value.map((o, i) => (
      <div key={i} className="flex gap-2 items-start">
        <input aria-label={`Outcome ${i + 1}`} className={inputClass} value={o.text} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
        <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap pt-2">
          <input type="checkbox" checked={o.isDraft} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, isDraft: e.target.checked } : x)))} />
          Draft
        </label>
        <button type="button" aria-label={`Remove outcome ${i + 1}`} className="text-gray-400 hover:text-red-600 pt-2 text-sm" onClick={() => onChange(value.filter((_, j) => j !== i))}>Remove</button>
      </div>
    ))}
    <button type="button" className="text-sm text-primary-700 hover:underline" onClick={() => onChange([...value, { text: '', isDraft: false }])}>+ Add outcome</button>
    <p className="text-xs text-gray-500">Draft outcomes are hidden from planters until you uncheck Draft.</p>
  </div>
);

export const SessionForm: React.FC<{
  initial?: TrainingSession | null;
  defaultStartTime: string;
  defaultDurationMinutes: number;
  submitLabel: string;
  pending: boolean;
  error: unknown;
  onSubmit: (input: SessionInput) => void;
  onCancel?: () => void;
}> = ({ initial, defaultStartTime, defaultDurationMinutes, submitLabel, pending, error, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    module: initial?.module || '',
    part: initial?.part ? String(initial.part) : '',
    title: initial?.title || '',
    date: initial?.date || '',
    startTime: initial?.startTime || defaultStartTime,
    durationMinutes: initial?.durationOverride ? String(initial.durationOverride) : '',
    venueId: initial?.venue?.id ?? null as number | null,
    description: initial?.description || '',
    notes: initial?.notes || '',
    outcomes: initial?.outcomes?.map((o) => ({ text: o.text, isDraft: o.isDraft })) || [] as TrainingOutcome[],
  });
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      module: form.module,
      part: form.part ? Number(form.part) : null,
      title: form.title,
      date: form.date,
      startTime: form.startTime,
      durationMinutes: form.durationMinutes ? Number(form.durationMinutes) : null,
      venueId: form.venueId,
      description: form.description || null,
      notes: form.notes || null,
      outcomes: form.outcomes.filter((o) => o.text.trim()),
    });
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        <label className="block text-sm sm:col-span-4"><span className="text-gray-700">Module</span>
          <input className={inputClass} required placeholder="e.g. Plant the Church" value={form.module} onChange={(e) => set({ module: e.target.value })} /></label>
        <label className="block text-sm sm:col-span-2"><span className="text-gray-700">Part</span>
          <input className={inputClass} type="number" min={1} placeholder="—" value={form.part} onChange={(e) => set({ part: e.target.value })} /></label>
        <label className="block text-sm sm:col-span-6"><span className="text-gray-700">Title</span>
          <input className={inputClass} required value={form.title} onChange={(e) => set({ title: e.target.value })} /></label>
        <label className="block text-sm sm:col-span-2"><span className="text-gray-700">Date</span>
          <input className={inputClass} type="date" required value={form.date} onChange={(e) => set({ date: e.target.value })} /></label>
        <label className="block text-sm sm:col-span-2"><span className="text-gray-700">Start time (Central)</span>
          <input className={inputClass} type="time" required value={form.startTime} onChange={(e) => set({ startTime: e.target.value })} /></label>
        <label className="block text-sm sm:col-span-2"><span className="text-gray-700">Length (minutes)</span>
          <input className={inputClass} type="number" min={15} placeholder={`${defaultDurationMinutes} (default)`} value={form.durationMinutes} onChange={(e) => set({ durationMinutes: e.target.value })} /></label>
      </div>
      <div className="text-sm"><span className="text-gray-700">Venue</span><VenuePicker value={form.venueId} onChange={(venueId) => set({ venueId })} /></div>
      <div className="text-sm"><span className="text-gray-700">Outcomes</span><OutcomesEditor value={form.outcomes} onChange={(outcomes) => set({ outcomes })} /></div>
      <label className="block text-sm"><span className="text-gray-700">Notes shown with the session</span>
        <textarea className={inputClass} rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></label>
      <div className="flex gap-2">
        <button className={buttonClass} type="submit" disabled={pending}>{pending ? 'Saving…' : submitLabel}</button>
        {onCancel && <button type="button" className={secondaryButtonClass} onClick={onCancel}>Cancel</button>}
      </div>
      <ErrorText error={error} fallback="Unable to save session" />
    </form>
  );
};

// ---------- Trainer slots ----------

export const TrainerSlots: React.FC<{
  session: TrainingSession;
  canAssign: boolean;
  onChanged?: () => void;
  compact?: boolean;
}> = ({ session, canAssign, onChanged, compact }) => {
  const { isTrainer, userId } = useTrainingRoles();
  const qc = useQueryClient();
  const trainers = useQuery({ queryKey: ['training', 'trainers'], queryFn: trainingService.trainers, enabled: canAssign });
  const done = () => {
    qc.invalidateQueries({ queryKey: ['training'] });
    onChanged?.();
  };
  const assign = useMutation({ mutationFn: ({ slot, id }: { slot: TrainerSlotName; id: number | null }) => trainingService.assignSlot(session.id, slot, id), onSuccess: done });
  const claim = useMutation({ mutationFn: (slot: TrainerSlotName) => trainingService.claimSlot(session.id, slot), onSuccess: done });
  const release = useMutation({ mutationFn: () => trainingService.releaseSlot(session.id), onSuccess: done });
  const mine = session.trainers.some((t) => t.trainer?.id === userId);
  const upcoming = new Date(session.startsAt) > new Date() && session.status !== 'cancelled';

  const slotView = (s: TrainerSlot) => (
    <div key={s.slot} className={`flex flex-wrap items-center gap-2 ${compact ? 'text-sm' : ''}`}>
      <span className="text-xs uppercase tracking-wide text-gray-500 w-10">{s.slot === 'lead' ? 'Lead' : 'Co'}</span>
      {canAssign ? (
        <select
          aria-label={`${s.slot === 'lead' ? 'Lead' : 'Co'} trainer`}
          className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
          value={s.trainer?.id ?? ''}
          disabled={assign.isPending}
          onChange={(e) => assign.mutate({ slot: s.slot, id: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">Open</option>
          {s.trainer && !(trainers.data || []).some((t) => t.id === s.trainer!.id) && <option value={s.trainer.id}>{s.trainer.name}</option>}
          {(trainers.data || []).map((t) => <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>)}
        </select>
      ) : s.trainer ? (
        <span className={s.trainer.id === userId ? 'font-semibold text-primary-700' : 'text-gray-800'}>{s.trainer.name}{s.trainer.id === userId ? ' (you)' : ''}</span>
      ) : (
        <span className="text-amber-700 font-medium">Open</span>
      )}
      {!canAssign && !s.trainer && isTrainer && !mine && upcoming && (
        <button className="text-sm px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50" disabled={claim.isPending} onClick={() => claim.mutate(s.slot)}>
          Claim
        </button>
      )}
      {s.trainer?.id === userId && upcoming && (
        <button className="text-xs text-gray-500 hover:text-red-600 underline" disabled={release.isPending}
          onClick={() => { if (window.confirm('Release this slot? Staff will be notified.')) release.mutate(); }}>
          Release
        </button>
      )}
    </div>
  );
  return (
    <div className="space-y-1">
      {session.trainers.map(slotView)}
      <ErrorText error={assign.error || claim.error || release.error} />
    </div>
  );
};

/** Trainer-only accounts have no dashboard data; send them to Training instead. */
export const TrainerHomeRedirect: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user } = useAuth();
  const roles = (user?.roles || []).map((r) => (typeof r === 'string' ? r : r.name));
  const otherHubRoles = ['admin', 'staff', 'hub_leader', 'assessor', 'coach', 'assessment-admin'];
  if (roles.includes('trainer') && !roles.some((r) => otherHubRoles.includes(r))) return <Navigate to="/training" replace />;
  return children;
};
